import { safeRandomUUID } from '#pplx/frontend/common/safeRandomUUID.js';
import { EVENT_PAYLOAD } from '#pplx/frontend/framework/instrumentation/types.js';
import { useInstrumentation } from '#pplx/frontend/framework/instrumentation/useInstrumentation.js';
import { useWebPlatform } from '#pplx/frontend/platform-components/useWebPlatform.js';
import { useCallback } from 'react';
/** Preserve the existing analytics buckets for sources without a dedicated provider value. */
export function fileUploadSourceType(source) {
    return source === 'google_drive' ||
        source === 'dropbox' ||
        source === 'sharepoint' ||
        source === 'local'
        ? source
        : 'other_connectors';
}
const fileUploadAttemptEvent = EVENT_PAYLOAD;
const FILE_EXTENSION_ALLOWLIST = [
    '3gp',
    '7z',
    'aac',
    'aiff',
    'avif',
    'avi',
    'bash',
    'bat',
    'bmp',
    'c',
    'coffee',
    'conf',
    'config',
    'cpp',
    'cs',
    'css',
    'csv',
    'cxx',
    'dart',
    'diff',
    'doc',
    'docx',
    'fish',
    'flac',
    'flv',
    'gif',
    'go',
    'gz',
    'h',
    'heic',
    'heif',
    'hpp',
    'htm',
    'html',
    'ico',
    'in',
    'ini',
    'ipynb',
    'java',
    'jpe',
    'jpeg',
    'jp2',
    'jpg',
    'js',
    'json',
    'jsx',
    'ksh',
    'kt',
    'kts',
    'latex',
    'less',
    'log',
    'lua',
    'm',
    'm4a',
    'markdown',
    'md',
    'mkv',
    'mov',
    'mp3',
    'mp4',
    'mpeg',
    'mpg',
    'odf',
    'odp',
    'ods',
    'odt',
    'ogg',
    'opus',
    'parquet',
    'pdf',
    'php',
    'pl',
    'pm',
    'png',
    'ppt',
    'pptx',
    'py',
    'r',
    'rar',
    'rb',
    'rmd',
    'rs',
    'rtf',
    'scala',
    'sh',
    'sql',
    'svg',
    'swift',
    't',
    'tar',
    'tex',
    'text',
    'tif',
    'tiff',
    'toml',
    'ts',
    'tsv',
    'tsx',
    'txt',
    'wav',
    'webm',
    'webp',
    'wmv',
    'xls',
    'xlsx',
    'xml',
    'yaml',
    'yml',
    'zip',
    'zsh',
];
const FILE_EXTENSION_ALLOWLIST_SET = new Set(FILE_EXTENSION_ALLOWLIST);
function isFileExtension(extension) {
    return FILE_EXTENSION_ALLOWLIST_SET.has(extension);
}
function allowlistedFileExtension(extension) {
    if (!extension)
        return null;
    const normalizedExtension = extension.toLowerCase();
    return isFileExtension(normalizedExtension) ? normalizedExtension : null;
}
export function fileExtension(filename) {
    const dot = filename.lastIndexOf('.');
    if (dot <= 0 || dot === filename.length - 1)
        return null;
    return allowlistedFileExtension(filename.slice(dot + 1));
}
export function useFileUploadAnalytics(surface) {
    const { device: { isMobile }, } = useWebPlatform();
    const platform = isMobile ? 'mobile_web' : 'web';
    const base = useCallback((attempt) => ({
        ...attempt,
        fileExtension: allowlistedFileExtension(attempt.fileExtension),
        platform,
        surface: surface ?? 'thread_attachment',
    }), [platform, surface]);
    const started = useCallback((attempt) => {
        if (!surface)
            return null;
        return fileUploadAttemptEvent({
            name: 'file upload attempt started',
            data: base(attempt),
        });
    }, [base, surface]);
    const completed = useCallback((attempt, outcome, errorCode) => {
        if (!surface)
            return null;
        return fileUploadAttemptEvent({
            name: 'file upload attempt completed',
            data: { ...base(attempt), outcome, errorCode: errorCode ?? null },
            once: { enabled: true, key: attempt.uploadAttemptUUID },
        });
    }, [base, surface]);
    const rejectInitiation = useCallback((candidates, sourceType, errorCode, selectionFileCount = candidates?.length ?? 1) => {
        if (!surface)
            return [];
        const rejectedCandidates = candidates?.length ? candidates : [null];
        return rejectedCandidates.flatMap((candidate) => {
            const attempt = {
                uploadAttemptUUID: safeRandomUUID(),
                fileUUID: null,
                sourceType,
                fileSizeBytes: candidate?.size ?? null,
                fileExtension: fileExtension(candidate?.name ?? ''),
                fileCount: selectionFileCount,
            };
            const startedPayload = started(attempt);
            const completedPayload = completed(attempt, 'failure', errorCode);
            return startedPayload && completedPayload ? [startedPayload, completedPayload] : [];
        });
    }, [completed, started, surface]);
    return { started, completed, rejectInitiation };
}
export function useTrackedFileUploadAnalytics(surface) {
    const { started: startedEvent, completed: completedEvent, rejectInitiation: rejectInitiationEvents, } = useFileUploadAnalytics(surface);
    const { track } = useInstrumentation();
    const started = useCallback((attempt) => {
        const event = startedEvent(attempt);
        if (event)
            track(event);
    }, [startedEvent, track]);
    const completed = useCallback((attempt, outcome, errorCode) => {
        const event = completedEvent(attempt, outcome, errorCode);
        if (event)
            track(event);
    }, [completedEvent, track]);
    const rejectInitiation = useCallback((candidates, sourceType, errorCode, selectionFileCount) => {
        rejectInitiationEvents(candidates, sourceType, errorCode, selectionFileCount).forEach(track);
    }, [rejectInitiationEvents, track]);
    return { started, completed, rejectInitiation };
}
//# sourceMappingURL=fileUploadAnalytics.js.map